import React, { useEffect } from 'react';
import styled from 'styled-components';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { IntlShape, injectIntl } from 'react-intl';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardTitle from 'containers/common/Card/CardTitle';
import TablePagination from '@material-ui/core/TablePagination';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import moment from 'moment-timezone';

import { translate } from 'containers/services/translate';
import messages from 'containers/PendingClaimsPage/messages';
import { ROW_PER_PAGE } from 'constants/global';
import NewSearchBar, { SearchQuery } from 'components/NewSearchBar';
import { pushTo } from 'containers/services/routes-helper';
import { PAGES } from 'routes';
import {
  requestPendingClaims,
  requestSearchPendingClaims,
} from 'containers/PendingClaimsPage/actions';
import {
  makeSelectPendingClaims,
  makeSelectCountPendingClaims,
} from 'containers/PendingClaimsPage/selectors';
import * as featureService from 'features/fflip-helpers';
import { FEATURES } from 'features/features';
import { CLAIM_STATUS } from 'containers/data-structure';
import { makeSelectListingsByField } from 'containers/AppContainer/selectors';
import { ClaimByStatus, SagisConfig } from 'containers/AppContainer/typings';
import { formatClaim } from 'containers/AppContainer/utils';
import { makeSelectKeycloakToken } from 'containers/AppContainer/selectors';
import { LISTINGS } from 'containers/data-structure';
import {
  SEARCH_QUERY_INIT_VALUES,
  SEARCH_TYPE_IDS,
} from 'components/NewSearchBar/constants';
import { SEARCH_TYPE } from 'containers/ValidatedClaimsPage/constants';

const Container = styled.div`
   {
    margin-top: 50px;
  }
  .title {
    text-align: left;
    font-family: Roboto;
    font-weight: Bold;
    font-size: 18px;
    letter-spacing: 0;
    color: #384264;
    opacity: 1;
    padding: 10px;
  }
  .headerTitle {
    text-align: left;
    font-size: 11px;
    letter-spacing: 0;
    color: #000000de;
    text-transform: none;
    opacity: 1;
    font-family: Roboto;
    padding-bottom: 0px;
    font-weight: Bold;
  }
  .row {
    border-radius: 4px;
    text-align: left;
    font-family: Roboto;
    letter-spacing: 0;
    font-size: 11px;
    color: #2b2727;
  }
  .claims {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
  }
  .pendingClaims {
    padding-top: 32px;
    width: 100%;
  }
  .header {
    display: flex;
    justify-content: space-between;
  }
`;

export interface PendingClaimsPageProps {
  intl: IntlShape;
  requestPendingClaims: Function;
  countClaims: number;
  pendingClaims: ClaimByStatus[];
  cities: SagisConfig[];
  keyclokToken: string | null;
  requestSearchPendingClaims: Function;
}

const PendingClaimsPage: React.FC<PendingClaimsPageProps> = (
  props,
): JSX.Element => {
  const [page, setPage] = React.useState(0);
  const [pageSize, setRowsPerPage] = React.useState(25);
  const [searchQuery, setSearchQuery] = React.useState<SearchQuery>(
    SEARCH_QUERY_INIT_VALUES,
  );
  /* istanbul ignore next */
  useEffect(() => {
    /* istanbul ignore next */
    if (props.keyclokToken)
      props.requestPendingClaims(CLAIM_STATUS.PENDING, page, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.keyclokToken]);

  /* istanbul ignore next */
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    props.requestPendingClaims(CLAIM_STATUS.PENDING, newPage, pageSize);
  };

  /* istanbul ignore next */
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const size = parseInt(event.target.value, 10);
    setRowsPerPage(size);
    props.requestPendingClaims(CLAIM_STATUS.PENDING, page, size);
  };

  const formattedPendingClaims = formatClaim(props.pendingClaims, props.cities);
  /* istanbul ignore next */
  const handlerSearch = () => {
    const query = {
      searchCriteria: {
        ...searchQuery,
        status: CLAIM_STATUS.PENDING,
      },
      searchType: SEARCH_TYPE.CLAIM,
    };
    props.requestSearchPendingClaims(query, props.keyclokToken);
  };
  /* istanbul ignore next */
  const handlerRefresh = () => {
    setSearchQuery(SEARCH_QUERY_INIT_VALUES);
    props.requestPendingClaims(CLAIM_STATUS.PENDING, page, pageSize);
  };
  const searchCriterias = [
    {
      id: SEARCH_TYPE_IDS.REGISTER,
      title: translate(messages.registrationNumber, props),
    },
    {
      id: SEARCH_TYPE_IDS.POLICY,
      title: translate(messages.policyNumber, props),
    },
    {
      id: SEARCH_TYPE_IDS.CLAIM,
      title: translate(messages.claimNumber, props),
    },
  ];
  return (
    <Container>
      <Card>
        <CardContent>
          <div className="header">
            <CardTitle
              count={props.countClaims}
              message={translate({ ...messages.title }, props)}
            />
            {featureService.hasFeature(FEATURES.SEARCH_BAR) && (
              <NewSearchBar
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                handlerSearch={handlerSearch}
                handlerRefresh={handlerRefresh}
                isHomePage
                showAll={false}
                customCriterias={searchCriterias}
              />
            )}
          </div>
          <div>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell align="left" className="headerTitle">
                    {translate({ ...messages.claimNumber }, props)}
                  </TableCell>
                  <TableCell align="left" className="headerTitle">
                    {translate({ ...messages.registrationNumber }, props)}
                  </TableCell>
                  <TableCell align="left" className="headerTitle">
                    {translate({ ...messages.openingDate }, props)}
                  </TableCell>
                  <TableCell align="left" className="headerTitle">
                    {translate({ ...messages.lossDate }, props)}
                  </TableCell>
                  <TableCell align="left" className="headerTitle">
                    {translate({ ...messages.claimPlace }, props)}
                  </TableCell>
                  <TableCell align="left" className="headerTitle">
                    {translate({ ...messages.lossHour }, props)}
                  </TableCell>
                  <TableCell align="left" className="headerTitle">
                    {translate({ ...messages.numberOfVictim }, props)}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* istanbul ignore next */
                formattedPendingClaims.map((row: ClaimByStatus) => (
                  <TableRow
                    key={row.id}
                    onClick={pushTo(`${PAGES.RECAP}/${row.id}`)}
                  >
                    <TableCell align="left" className="row">
                      {row.claimNumber}
                    </TableCell>
                    <TableCell align="left" className="row">
                      {row.registrationNumber}
                    </TableCell>
                    <TableCell align="left" className="row">
                      {moment(row.createdAt).format('YYYY-MM-DD')}
                    </TableCell>
                    <TableCell align="left" className="row">
                      {moment(row.lossDate).format('YYYY-MM-DD')}
                    </TableCell>
                    <TableCell align="left" className="row">
                      {row.lossLocationLabel}
                    </TableCell>
                    <TableCell align="left" className="row">
                      {row.lossHour}
                    </TableCell>
                    <TableCell align="left" className="row">
                      {row.victims.length}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={ROW_PER_PAGE}
              component="div"
              count={props.countClaims}
              rowsPerPage={pageSize}
              page={page}
              labelRowsPerPage={translate({ ...messages.linesPerPage }, props)}
              onChangePage={handleChangePage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
            />
          </div>
        </CardContent>
      </Card>
    </Container>
  );
};

const mapDispatchToProps = {
  requestPendingClaims,
  requestSearchPendingClaims,
};

const mapStateToProps = createStructuredSelector({
  pendingClaims: makeSelectPendingClaims(),
  countClaims: makeSelectCountPendingClaims(),
  cities: makeSelectListingsByField(LISTINGS.CITIES),
  keyclokToken: makeSelectKeycloakToken(),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(injectIntl(PendingClaimsPage));
